import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/homeData";

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  const closeAll = () => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 h-16 flex items-center transition-all duration-300 ${
          scrolled
            ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
            : "bg-black/20 backdrop-blur-sm"
        }`}
      >
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 flex items-center justify-between">
          <Link
            to="/"
            className={`text-lg md:text-xl font-heading font-bold tracking-tight whitespace-nowrap transition-colors duration-300 ${
              scrolled ? "text-foreground-950" : "text-white drop-shadow-md"
            }`}
            onClick={closeAll}
          >
            oopbuyspreadsheets.pics
          </Link>

          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/"
              className={`text-sm font-medium whitespace-nowrap transition-colors duration-300 cursor-pointer ${
                scrolled
                  ? "text-foreground-800 hover:text-primary-600"
                  : "text-white/95 hover:text-white drop-shadow-sm"
              }`}
            >
              Home
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                  scrolled
                    ? "bg-foreground-950 text-background-50 hover:bg-foreground-800"
                    : "bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 border border-white/30"
                }`}
              >
                Categories
                <i
                  className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform duration-200 ${
                    dropdownOpen ? "rotate-180" : ""
                  }`}
                ></i>
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-background-50 rounded-lg border border-background-200/70 shadow-lg py-1 max-h-[420px] overflow-y-auto">
                  {categories.map((cat, idx) => (
                    <a
                      key={idx}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-800 hover:bg-accent-100 hover:text-accent-900 transition-colors duration-150 cursor-pointer border-b border-background-100 last:border-b-0"
                      onClick={() => setDropdownOpen(false)}
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className={`inline-flex items-center gap-1.5 text-sm font-medium whitespace-nowrap transition-colors duration-300 cursor-pointer ${
                scrolled
                  ? "text-foreground-800 hover:text-primary-600"
                  : "text-white/90 hover:text-white drop-shadow-sm"
              }`}
            >
              Shopping Guide
              <i className="ri-external-link-line w-4 h-4 flex items-center justify-center"></i>
            </a>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-1.5 bg-accent-500 text-white px-5 py-2 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-accent-600 transition-all duration-200"
            >
              Buy It Now
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
            </a>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden flex items-center justify-center w-10 h-10 rounded-lg cursor-pointer transition-colors duration-200 ${
              scrolled ? "text-foreground-950 hover:bg-background-100" : "text-white hover:bg-white/10"
            }`}
            aria-label="Toggle menu"
          >
            <i
              className={`${
                mobileMenuOpen ? "ri-close-line" : "ri-menu-line"
              } text-xl w-6 h-6 flex items-center justify-center`}
            ></i>
          </button>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-[60]">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={closeAll}
          ></div>

          <div className="absolute top-16 left-0 right-0 bottom-0 bg-background-50 overflow-y-auto animate-slide-down">
            <div className="px-4 py-6 flex flex-col gap-4">
              <Link
                to="/"
                onClick={closeAll}
                className="flex items-center gap-3 px-4 py-3 rounded-xl bg-background-100 text-foreground-900 font-semibold cursor-pointer active:scale-[0.98] transition-transform"
              >
                <i className="ri-home-4-line w-5 h-5 flex items-center justify-center text-accent-600"></i>
                Home
              </Link>

              <div className="px-4 py-2">
                <span className="text-xs font-semibold text-foreground-500 uppercase tracking-widest">
                  Categories
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 px-4">
                {categories.map((cat, idx) => (
                  <a
                    key={idx}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block px-3 py-2.5 rounded-lg text-sm text-foreground-800 bg-background-100 hover:bg-accent-100 hover:text-accent-900 transition-colors duration-150 cursor-pointer active:scale-[0.97]"
                    onClick={closeAll}
                  >
                    {cat.name}
                  </a>
                ))}
              </div>

              <div className="border-t border-background-200/70 my-2"></div>

              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-3 px-4 py-3 rounded-xl bg-background-100 text-foreground-900 font-medium cursor-pointer active:scale-[0.98] transition-transform"
                onClick={closeAll}
              >
                <i className="ri-shopping-bag-3-line w-5 h-5 flex items-center justify-center text-accent-600"></i>
                Shopping Guide
              </a>

              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 mx-4 mt-2 bg-accent-500 text-white px-6 py-3.5 rounded-full text-base font-semibold cursor-pointer hover:bg-accent-600 active:scale-[0.98] transition-all duration-200"
                onClick={closeAll}
              >
                Buy It Now
                <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}